import Blue from "~/core";

/**
 * Hoverable Feature Card with Zoom Preview
 *
 * Creates an interactive zoom preview that appears next to the feature card image
 * when hovering over it. Shows a magnified view of the area under the cursor.
 *
 * Usage: Add the class "feature-block--hoverable" to any feature block
 *
 * Customization:
 * - previewSize: Size of the zoom preview box in pixels
 * - zoomLevel: Magnification level
 */
new Blue({
  name: "Hoverable Feature Block",
  selector: ".feature-block--hoverable",
  setup(hoverableFeatureBlock) {
    const previewSize =
      parseInt(hoverableFeatureBlock.dataset.previewSize) || 500;
    const zoomLevel = parseInt(hoverableFeatureBlock.dataset.zoomLevel) || 3;

    const mediaContainer = hoverableFeatureBlock.querySelector(
      ".feature-block__media"
    );
    if (!mediaContainer) return;

    const swiperContainer = mediaContainer.querySelector(".swiper-container") 
      || mediaContainer.querySelector(".swiper");
    if (!swiperContainer?.swiper) return;
    const { swiper } = swiperContainer;

    const swiperSlides = [
      ...mediaContainer.querySelectorAll(".swiper-slide picture img"),
    ];

    swiper.on("slideChange", () => removeZoomPreview(0));
    swiperSlides.forEach((slide) => {
      slide.addEventListener("mouseenter", () => createZoomPreview());
      slide.addEventListener("mousemove", (e) => updateZoomPreview(e));
      slide.addEventListener("mouseleave", () => removeZoomPreview(100));
    });

    let imgEl = null;
    let imageRect = null;
    let zoomPreview = null;
    let zoomImage = null;
    let hoverIndicator = null;
    let previewRemovalTimeout = null;
    let indicatorRemovalTimeout = null;

    const createZoomPreview = () => {
      if (previewRemovalTimeout) {
        clearTimeout(previewRemovalTimeout);
        previewRemovalTimeout = null;
      }

      if (indicatorRemovalTimeout) {
        clearTimeout(indicatorRemovalTimeout);
        indicatorRemovalTimeout = null;
      }

      if (zoomPreview && hoverIndicator) {
        zoomPreview.style.opacity = "1";
        hoverIndicator.style.opacity = "1";
        return;
      }

      imgEl = getCurrentImage();
      if (!imgEl) return;

      // Calculate how the main image is rendered (object-fit: contain) to size indicator correctly
      imageRect = imgEl.getBoundingClientRect();
      const aspectRatio = imgEl.naturalWidth / imgEl.naturalHeight;

      const rendered = calculateContainedImageDimensions(
        aspectRatio,
        imageRect.width,
        imageRect.height
      );
      const indicatorSize = rendered.width / zoomLevel;

      zoomPreview = document.createElement("div");
      zoomPreview.className = "feature-block__zoom-preview";
      zoomPreview.style.width = `${previewSize}px`;
      zoomPreview.style.height = `${previewSize}px`;

      zoomImage = document.createElement("img");
      zoomImage.className = "feature-block__zoom-preview--image";
      zoomImage.style.transformOrigin = "0 0";
      zoomImage.src = imgEl.currentSrc || imgEl.src;

      hoverIndicator = document.createElement("div");
      hoverIndicator.className = "feature-block__zoom-indicator";
      hoverIndicator.style.width = `${indicatorSize}px`;
      hoverIndicator.style.height = `${indicatorSize}px`;

      zoomPreview.appendChild(zoomImage);
      mediaContainer.appendChild(zoomPreview);
      imgEl.parentElement.parentElement.appendChild(hoverIndicator);
    };

    const updateZoomPreview = (e) => {
      if (!zoomPreview || !zoomImage || !hoverIndicator) return;

      // Calculate how the main image is actually rendered (object-fit: contain)
      imageRect = imgEl.getBoundingClientRect();
      const aspectRatio = imgEl.naturalWidth / imgEl.naturalHeight;

      const rendered = calculateContainedImageDimensions(
        aspectRatio,
        imageRect.width,
        imageRect.height
      );

      const {
        width: renderedWidth,
        height: renderedHeight,
        offsetX: renderedOffsetX,
        offsetY: renderedOffsetY,
      } = rendered;

      // Get mouse position relative to container
      const mouseX = e.clientX - imageRect.left;
      const mouseY = e.clientY - imageRect.top;

      // Position indicator relative to rendered image
      const indicatorSize = hoverIndicator.clientWidth;
      const halfIndicator = indicatorSize / 2;

      // Calculate position within rendered image bounds
      const mouseXInRendered = mouseX - renderedOffsetX;
      const mouseYInRendered = mouseY - renderedOffsetY;

      const borderAdjustment = 4;
      const indicatorLeftInRendered = Math.max(
        0,
        Math.min(
          mouseXInRendered - halfIndicator,
          renderedWidth - indicatorSize - borderAdjustment
        )
      );
      const indicatorTopInRendered = Math.max(
        0,
        Math.min(
          mouseYInRendered - halfIndicator,
          renderedHeight - indicatorSize - borderAdjustment
        )
      );

      // Convert back to container coordinates for positioning
      const indicatorLeft = indicatorLeftInRendered + renderedOffsetX;
      const indicatorTop = indicatorTopInRendered + renderedOffsetY;

      // Calculate what percentage of the actual rendered image we're viewing
      const xPercent = (indicatorLeftInRendered + halfIndicator) / renderedWidth;
      const yPercent = (indicatorTopInRendered + halfIndicator) / renderedHeight;
      const previewCenter = previewSize / 2;

      const preview = calculateContainedImageDimensions(
        aspectRatio,
        previewSize,
        previewSize
      );

      const {
        width: previewImageWidth,
        height: previewImageHeight,
        offsetX,
        offsetY,
      } = preview;

      // Calculate position in preview space and center it
      const pointX = offsetX + xPercent * previewImageWidth;
      const pointY = offsetY + yPercent * previewImageHeight;
      const translateX = previewCenter - pointX * zoomLevel;
      const translateY = previewCenter - pointY * zoomLevel;

      zoomPreview.style.opacity = "1";

      zoomImage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${zoomLevel})`;

      hoverIndicator.style.left = `${indicatorLeft}px`;
      hoverIndicator.style.top = `${indicatorTop}px`;
      hoverIndicator.style.opacity = "1";
    };

    const removeZoomPreview = (timeout) => {
      if (zoomPreview) {
        zoomPreview.style.opacity = "0";
        previewRemovalTimeout = setTimeout(() => {
          zoomPreview.remove();
          zoomPreview = null;
          zoomImage = null;
          previewRemovalTimeout = null;
        }, timeout);
      }
      if (hoverIndicator) {
        hoverIndicator.style.opacity = "0";
        indicatorRemovalTimeout = setTimeout(() => {
          hoverIndicator.remove();
          hoverIndicator = null;
          indicatorRemovalTimeout = null;
        }, timeout);
      }
    };

    const getCurrentImage = () => {
      const activeSlide = mediaContainer.querySelector(
        ".swiper-slide-active picture img"
      );
      return activeSlide;
    };

    const calculateContainedImageDimensions = (
      aspectRatio,
      containerWidth,
      containerHeight
    ) => {
      const containerAspect = containerWidth / containerHeight;
      if (aspectRatio > containerAspect) {
        const width = containerWidth;
        const height = containerWidth / aspectRatio;
        return {
          width,
          height,
          offsetX: 0,
          offsetY: (containerHeight - height) / 2,
        };
      } else {
        const width = containerHeight * aspectRatio;
        const height = containerHeight;
        return {
          width,
          height,
          offsetX: (containerWidth - width) / 2,
          offsetY: 0,
        };
      }
    };
  },
});
